import React, { useState, useEffect, useCallback } from 'react';
import '../TimeKeeping.css';
import '../../../App.css';
import 'react-calendar/dist/Calendar.css';
import { CreateButton } from '../../CommonObjects';
import { SetAuditTrail } from '../../Reports/AuditTrailReportEmployeeSetup';
import axios from '../../../api/apiClient';
import { token, domain } from '../../Security';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { LoadingSpinner } from '../../../common/Loading';
import { ConfirmationModal } from '../../../common/Modal';

// icons
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CreateTableNew as CreateTable, SearchBar } from '../../../common/APICommon';

// Import pdf.js
import * as pdfjsLib from 'pdfjs-dist';
import { FEATURES } from '../../../config/features';
import { DynamicFilterBar } from '../../../v2/components/filters/DynamicFilterBar';
import { useFilterManager } from '../../../v2/hooks/useFilterManager';
import { CommonFilterIds } from '../../../utils/enums';
// Set up the worker for PDF parsing
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const UploadTimeEntriesERE = ({ user, userAccess, isHideSideMenu }) => {
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [isDatUploadLoading, setIsDatUploadLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    additionalApiFilterPayload,
    applyFilters,
    resetFilters,
    // getFiltersAsDetails
  } = useFilterManager(FEATURES.UPLOAD_TIME_ENTRIES_FILTER);

  // Data states
  const [rawTimeLogData, setRawTimeLogData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [uploadTimeEntriesDatFile, setUploadTimeEntriesDatFile] = useState([]);

  // Filter states - Only date range
  const [dateStartFilter, setDateStartFilter] = useState(new Date().toISOString().split('T')[0]);
  const [dateEndFilter, setDateEndFilter] = useState(new Date().toISOString().split('T')[0]);
  const [isGenerateTimeLogs, setIsGenerateTimeLogs] = useState(false);

  // Request messages
  const [showRequestMessage, setShowRequestMessage] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState("");

  // Pagination for main table
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination for upload modal table
  const [currentPageUpload, setCurrentPageUpload] = useState(1);

  useEffect(() => {
    if (showRequestMessage) {
      const timeoutId = setTimeout(() => {
        setShowRequestMessage(false);
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [showRequestMessage]);

  // Table configuration for main table
  const mainTableHeaders = [
    "ID",
    "Employee ID",
    "Employee Name",
    "Date",
    "Time",
    "Branch / Serial No",
  ];

  const mainTableAccessors = [
    "id",
    "userId",
    "name",
    "date",
    "time",
    "serialNumber"
  ];
  

  // Table configuration for upload preview
  const uploadTableHeaders = [
    "Name",
    "Enroll #",
    "Department",
    "Branch",
    "Date",
    "Day",
    "Time In",
    "TimeOut",
    "Time In",
    "TimeOut",
    "Time In",
    "TimeOut",
  ];

  const uploadTableAccessors = [
    "name",
    "enrollNo",
    "department",
    "branch",
    "date",
    "day",
    "timeIn_1",
    "timeOut_1",
    "timeIn_2",
    "timeOut_2",
    "timeIn_3",
    "timeOut_3",
  ];

  // Custom cell renderer for date time formatting
  const customCellRenderer = (row, accessor) => {
    const value = row[accessor];
    
    // Format date time
    if ((accessor === "dateTimeLog" || accessor === "dateTimeLog") && value) {
      return formatDateTimeForUI(value);
    }
    
    // Format error messages
    if (accessor === "errors" && value) {
      return <span className="text-red-600">{value}</span>;
    }

    if (accessor === 'date') {
      const _value = row.logTime; // e.g., "2026-05-16T05:13:26"
      if (!_value || _value === '-') return '-';

      // Split the string into Date and Time parts
      // datePart = "2026-05-16", timePart = "05:13:26"
      const datePart = _value.split('T')[0];

      const [yyyy, mm, dd] = datePart.split('-');
      return `${mm}/${dd}/${yyyy}`; // Result: 05/16/2026
    }

    if (accessor === 'time') {
      const _value = row.logTime; // e.g., "2026-05-16T05:13:26"
      if (!_value || _value === '-') return '-';

      const timePart = _value.split('T')[1];

      if (!timePart) return '-';
      
      let [hh, mm] = timePart.split(':');
      let hours = parseInt(hh);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      // Convert to 12-hour format and remove leading zero from hour
      hours = hours % 12 || 12; 
      
      return `${hours}:${mm} ${ampm}`; // Result: 5:13 AM
    }
    
    return value || '-';
  };

  const formatDateTimeForUI = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return dateTimeString;
    }
  };

  // Fetch all employees
  const fetchAllEmployees = async () => {
    const apiUrl = domain + "/api/Employee/masterlist";
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching data:', error);
      return [];
    }
  };

  // Fetch raw time logs data based on date range
  const fetchRawTimeLogsDynamicFilter = useCallback(async () => {
    if (!isGenerateTimeLogs) {
      return;
    }

    if (!additionalApiFilterPayload?.dateFrom || !additionalApiFilterPayload?.dateTo) {
      setShowRequestMessage(true);
      setIsSuccess(false);
      setRequestMessage("Please input date from and to");
      setIsGenerateTimeLogs(false);
      return;
    }

    // Format dates to YYYY-MM-DD
    const formattedStartDate = additionalApiFilterPayload?.dateFrom;
    const formattedEndDate = additionalApiFilterPayload?.dateTo;

    // Validate date range
    if (new Date(formattedStartDate) > new Date(formattedEndDate)) {
      setShowRequestMessage(true);
      setIsSuccess(false);
      setRequestMessage("Start date cannot be after end date");
      setIsGenerateTimeLogs(false);
      return;
    }

    const apiUrlFetch = domain + `/api/EmployeeTimeRecord/chronix-time-log-raw`;
    
    const selectLogs = {
      dateFrom: additionalApiFilterPayload?.dateFrom,
      dateTo: additionalApiFilterPayload?.dateTo,
      classificationId: apiFilterPayload?.find(f => f.type === CommonFilterIds.CLASSIFICATION)?.value || 0,
      companyId: apiFilterPayload?.find(f => f.type === CommonFilterIds.COMPANY)?.value || 0,
      clientId: apiFilterPayload?.find(f => f.type === CommonFilterIds.CLIENT)?.value || 0,
      departmentId: apiFilterPayload?.find(f => f.type === CommonFilterIds.DEPARTMENT)?.value || 0,
    }

    try {
      setLoadingRequest(true);
      const response = await axios.post(apiUrlFetch, selectLogs, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setRawTimeLogData(response.data);
      setFilterData(response.data);
      setIsGenerateTimeLogs(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setShowRequestMessage(true);
      setIsSuccess(false);
      setRequestMessage("Error fetching time logs data");
      setLoadingRequest(false);
    } finally {
      setLoadingRequest(false);
    }
  }, [additionalApiFilterPayload, apiFilterPayload, isGenerateTimeLogs]);
  const fetchRawTimeLogsDefault = useCallback(async () => {
    if (!isGenerateTimeLogs) {
      return;
    }

    // Format dates to YYYY-MM-DD
    const formattedStartDate = dateStartFilter;
    const formattedEndDate = dateEndFilter;

    // Validate date range
    if (new Date(formattedStartDate) > new Date(formattedEndDate)) {
      setShowRequestMessage(true);
      setIsSuccess(false);
      setRequestMessage("Start date cannot be after end date");
      setIsGenerateTimeLogs(false);
      return;
    }

    const apiUrlFetch = domain + `/api/EmployeeTimeRecord/chronix-time-log-raw`;

    const selectLogs = {
      dateFrom: dateStartFilter,
      dateTo: dateEndFilter,
    }

    try {
      setLoadingRequest(true);
      const response = await axios.post(apiUrlFetch, selectLogs, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setRawTimeLogData(response.data);
      setFilterData(response.data);
      setIsGenerateTimeLogs(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setShowRequestMessage(true);
      setIsSuccess(false);
      setRequestMessage("Error fetching time logs data");
      setLoadingRequest(false);
    } finally {
      setLoadingRequest(false);
    }
  }, [dateStartFilter, dateEndFilter, isGenerateTimeLogs]);
  const fetchRawTimeLogs = useCallback(async () => {
    if(FEATURES.IS_DYNAMIC_FILTER){
      await fetchRawTimeLogsDynamicFilter();
    } else {
      await fetchRawTimeLogsDefault();
    }
  }, [fetchRawTimeLogsDynamicFilter, fetchRawTimeLogsDefault]);

  // Handle file upload
  const handleFileDatFileUpload = async (files) => {
    try {
      setIsDatUploadLoading(true);
      if (!files || files.length === 0) {
        console.error('No files selected');
        return;
      }
      
      setUploadTimeEntriesDatFile([]); // reset before new upload
      
      const employees = await fetchAllEmployees();

      for (const file of files) {
        uploadTimeEntriesDatFileFunc(file, employees);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsDatUploadLoading(false);
    }
  };

  const uploadTimeEntriesDatFileFunc = (file, employeeList) => {
    setLoadingRequest(true);
    try {
      const employees = employeeList;

      const deviceNumberMap = employees.reduce((acc, emp) => {
        if (emp.deviceNumber) {
          acc[emp.deviceNumber] = {
            id: emp.id,
            employeeId: emp.employeeId,
            fullName: emp.fullName,
          };
        }
        return acc;
      }, {});

      const reader = new FileReader();

      reader.onload = (event) => {
        const text = event.target.result;

        const lines = text
          .split(/\r?\n/)
          .map(l => l.trim())
          .filter(Boolean);

        const parsedLogs = [];

        for (const line of lines) {
          const parts = line.split(/\s+/);

          // Expected format: deviceNumber date time deviceSerial
          if (parts.length < 4) {
            parsedLogs.push({
              errors: `Invalid format: ${line}`,
              originalLine: line,
            });
            continue;
          }

          const deviceNumber = parts[0];
          const datePart = parts[1];
          const timePart = parts[2];
          const deviceSerialNumber = parts[3];

          // Parse datetime safely
          const dateTime = convertToTimeDat(timePart, datePart);
          const employeeInfo = deviceNumberMap[deviceNumber];

          parsedLogs.push({
            // ✅ API payload fields
            // If employeeInfo is undefined, accessNumber is null. Backend should accept null or handle logic.
            accessNumber: employeeInfo?.employeeId ?? null, 
            deviceNo: deviceNumber,
            dateTimeLog: dateTime,
            deviceSerialNumber: deviceSerialNumber || "DAT Upload",

            // ✅ UI-only fields
            // If not found, show deviceNumber as ID and "Unknown" as name, but treat as Valid
            employeePersonalInformationEmployeeId: employeeInfo?.employeeId || deviceNumber,
            employeePersonalInformationFullName: employeeInfo?.fullName || "Unknown (Will Insert)",

            errors: null, // Keep errors null so it passes the !item.errors check
            originalLine: line,
          });
        }

        setUploadTimeEntriesDatFile(prev => [...prev, ...parsedLogs]);
      };

      reader.readAsText(file);
    } catch (error) {
      console.error("Error reading .dat file:", error);
    } finally {
      setLoadingRequest(false);
    }
  };

  // Handle final upload to API for TimeLogRaw
  const handleFinalDatFileUpload = async (bulkData) => {
    setLoadingRequest(true);

    var dataToPost = []

    // combine "05/16/2026" + "09:57 AM"
    const formatDateTimeLocal = (dateStr, timeStr) => {
      if (!timeStr || timeStr.trim() === "") return null;

      // 1. Parse Date (MM/DD/YYYY)
      const [month, day, year] = dateStr.split('/');
      
      // 2. Parse Time (HH:mm AM/PM)
      const match = timeStr.match(/(\d+):(\d+)\s+(AM|PM)/i);
      if (!match) return null;

      let hours = parseInt(match[1]);
      const minutes = match[2];
      const ampm = match[3].toUpperCase();

      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;

      const formattedHours = hours.toString().padStart(2, '0');
      
      // Returns format: "2026-05-16 09:57:00" (SQL Server loves this)
      return `${year}-${month}-${day}T${formattedHours}:${minutes}:00`;
    };

    bulkData.forEach((item, index) => {
      const nowTimestamp = Math.floor(Date.now() / 1000);

      const addLog = (timeValue, status) => {
        if (timeValue && timeValue.trim() !== "") {

          const localLogTime = formatDateTimeLocal(item.date, timeValue);

          dataToPost.push({
            // Using the current timestamp + index to ensure uniqueness if needed
            transactionId: nowTimestamp + index, 
            logTime: localLogTime,
            userId: parseInt(item.enrollNo),
            attendanceStatus: status,
            terminalType: "M50",
            terminalID: 1,
            serialNumber: item.branch,
          });
        }
      };

      // Process all 6 possible slots
      addLog(item.timeIn_1, "IN");
      addLog(item.timeOut_1, "OUT");
      addLog(item.timeIn_2, "BREAK_IN");
      addLog(item.timeOut_2, "BREAK_OUT");
      addLog(item.timeIn_3, "IN");
      addLog(item.timeOut_3, "OUT");
    })

    const apiUrl = domain + "/api/EmployeeTimeRecord/chronix-upload-time-log-raw";
    try {
      await axios.post(apiUrl, dataToPost, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await SetAuditTrail({
        setupArea: "Time Logs",
        actionTaken: "insert",
        field: "Upload Time Logs via .pdf File",
        newValue: "-",
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      await fetchRawTimeLogs();

      setShowRequestMessage(true);
      setIsSuccess(true);
      setRequestMessage("PDF file uploaded successfully!");
    } catch (error) {
      console.error('Error posting data to API:', error);
      setShowRequestMessage(true);
      setIsSuccess(false);

      if (error.response?.data?.errors?.["$"]?.[0]) {
        setRequestMessage(error.response.data.errors["$"][0]);
      } else if (error.response?.data?.message) {
        setRequestMessage(error.response.data.message);
      } else {
        setRequestMessage("An unknown error occurred.");
      }
    } finally {
      setLoadingRequest(false);
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /* const handleDeleteAll = async (event) => {
    event.preventDefault();

    if (rawTimeLogData.length === 0) {
      setShowRequestMessage(true);
      setIsSuccess(false);
      setRequestMessage("No time logs to delete");
      return;
    }

    // Show confirmation modal instead of window.confirm
    setShowDeleteConfirm(true);
  }; */

  const confirmDelete = async () => {
    setLoadingRequest(true);
    setShowDeleteConfirm(false);

    const apiUrl = domain + `/api/TimeLogRaw/date-range?startDate=${dateStartFilter}&endDate=${dateEndFilter}`;

    try {
      console.log("Sending DELETE request for time logs");
      await axios.delete(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      await SetAuditTrail({
        setupArea: "Time Logs",
        actionTaken: "delete",
        field: "Delete All Time Logs",
        newValue: "-",
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      // Clear the data after successful deletion
      setRawTimeLogData([]);
      setFilterData([]);

      setShowRequestMessage(true);
      setIsSuccess(true);
      setRequestMessage("All time logs deleted successfully!");
    } catch (error) {
      console.error('Error deleting entries:', error);
      setShowRequestMessage(true);
      setIsSuccess(false);

      if (error.response) {
        const errorMessage = error.response.data?.message || 'An error occurred while deleting.';
        setRequestMessage(errorMessage);
      } else if (error.request) {
        setRequestMessage("No response received from the server.");
      } else {
        setRequestMessage("An error occurred while processing your request.");
      }
    } finally {
      setLoadingRequest(false);
    }
  };

  // Generate data
  const generateTimeLogs = useCallback(() => {
    setIsGenerateTimeLogs(true);
  }, []);

  // Open/close upload modal
  const openUploadFormHandleClick = () => {
    setShowUploadForm(true);
  };

  const closeUploadFormHandleClick = () => {
    setShowUploadForm(false);
    setUploadTimeEntriesDatFile([]);
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (rawTimeLogData.length === 0) {
      setShowRequestMessage(true);
      setIsSuccess(false);
      setRequestMessage("No data to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet();

    const header = [
      'Employee ID',
      'Employee Name',
      'Date',
      'Time',
      'Branch / Serial No',
    ];

    const headerRow = worksheet.addRow(header);
    worksheet.columns = [
      { key: 'userId', width: 20 },
      { key: 'name', width: 30 },
      { key: 'logTime', width: 25 },
      { key: 'logTime', width: 25 },
      { key: 'serialNumber', width: 20 },
    ];

    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '92d050' },
      };
    });

    const formatDate = (_value) => {
      if (!_value || _value === '-') return '-';

      // Split the string into Date and Time parts
      // datePart = "2026-05-16", timePart = "05:13:26"
      const datePart = _value.split('T')[0];

      const [yyyy, mm, dd] = datePart.split('-');
      return `${mm}/${dd}/${yyyy}`; // Result: 05/16/2026
    }

    const formatTime = (_value) => {
      if (!_value || _value === '-') return '-';

      const timePart = _value.split('T')[1];

      if (!timePart) return '-';
      
      let [hh, mm] = timePart.split(':');
      let hours = parseInt(hh);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      // Convert to 12-hour format and remove leading zero from hour
      hours = hours % 12 || 12; 
      
      return `${hours}:${mm} ${ampm}`; // Result: 5:13 AM
    }

    rawTimeLogData.forEach((item) => {
      worksheet.addRow([
        item.userId || '',
        item.name || '',
        formatDate(item.logTime),
        formatTime(item.logTime),
        item.serialNumber || '',
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `TimeLogs_${dateStartFilter}_to_${dateEndFilter}_Export.xlsx`);
  };

  // Handle search filter
  const handleSearchFilter = (value) => {
    setCurrentPage(1); // Reset to first page when filtering
    const lowercaseValue = value.toLowerCase();

    if (!value) {
      // If input is empty, reset to original list
      setRawTimeLogData(filterData);
      return;
    }

    const res = filterData.filter((item) => {
        const match1 = item.name?.toLowerCase().includes(lowercaseValue);
        const match2 = item.userId?.toString().includes(lowercaseValue);

        return match1 || match2;
      }
    );
    setRawTimeLogData(res);
  };

  // Fetch data when isGenerateTimeLogs changes
  useEffect(() => {
    if (isGenerateTimeLogs) {
      fetchRawTimeLogs();
    }
  }, [isGenerateTimeLogs, fetchRawTimeLogs]);

  console.warn("-----------");
  console.warn("Upload Dat File : Update this table to have a limited, like 10 items per page");
  console.warn("-----------");

  const [shouldGenerate, setShouldGenerate] = useState(false);
  
  const finalSubmit = () => {
      setShouldGenerate(true);
      applyFilters();
  };

  useEffect(() => {
      if (!shouldGenerate) return;

      generateTimeLogs();
      setShouldGenerate(false);
  }, [additionalApiFilterPayload, shouldGenerate, generateTimeLogs]);

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="TimeInfo shadow-xl">
            <div className='sub-title row' style={{ padding: "5px 20px" }}>
              <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Time Keeping >"} </span>
              <span style={{ borderBottom: "1px solid black" }}>{"Upload DAT File"}</span>
            </div>

            <div className="timeHeader row" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <div className="tHeaderTitle row">
                <FileUploadIcon style={{ padding: "0 10px 0 0" }} />
                <h3>Upload Time Entries ERE</h3>
              </div>
            </div>

            <div className="mainData">
              {/* Filter Section - Only date range */}
              { FEATURES.IS_DYNAMIC_FILTER ?
                <div className='filters-payroll'>
                  <div className="">
                    <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm" >
                      <span className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4 block">Filter</span>
                      <DynamicFilterBar
                          requestedFilters={FEATURES.UPLOAD_TIME_ENTRIES_FILTER}
                          activeFilterState={activeFilterState}
                          onFilterChange={onFilterChange}
                          onApply={applyFilters}
                          onClear={resetFilters}
                          allowAll={false}
                          showButtons={false}
                      />
                    </div>
                  </div>
                  <div className="rightSide-btn" style={{ marginRight: "5px" }}>
                    {CreateButton("", "generate row", finalSubmit, "GENERATE", RestartAltIcon)}
                  </div>
                </div>
              :
                <div className='' style={{ padding: "10px" }}>
                  <div className='filters-payroll'>
                    <span className="text-gray-700 font-medium">Filter</span>
                    <div className="payroll-item-holder info-item-holder row" style={{ justifyContent: "unset", padding: "0 10px 20px 10px" }}>
                      <div className="filter-item-payroll popUp-item info-item column" style={{ marginRight: "15px" }}>
                        <div className="info-item-label text-gray-600 text-sm mb-1">Date Start</div>
                        <div className="info-item-input">
                          <input
                            type="date"
                            value={dateStartFilter}
                            onChange={(e) => setDateStartFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="filter-item-payroll popUp-item info-item column" style={{ marginRight: "15px" }}>
                        <div className="info-item-label text-gray-600 text-sm mb-1">Date End</div>
                        <div className="info-item-input">
                          <input
                            type="date"
                            value={dateEndFilter}
                            onChange={(e) => setDateEndFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rightSide-btn" style={{ marginRight: "5px" }}>
                      {CreateButton("", "generate row", generateTimeLogs, "GENERATE", RestartAltIcon)}
                    </div>
                  </div>
                </div>
              }

              {/* Action Buttons */}
              <div className="timeBtns row">
                <div className="timeBtn upload row">
                  {CreateButton("", "icon row", openUploadFormHandleClick, "UPLOAD FILE", FileUploadIcon)}
                </div>
                <div className="timeBtn extract row">
                  {CreateButton("", "icon row", exportToExcel, "EXPORT TO EXCEL", DownloadIcon)}
                </div>
              </div>

              {/* Search Bar - Using reusable SearchBar */}
              <div className="mainte-body-top row">
                <SearchBar handleFilter={handleSearchFilter} />
              </div>

              {/* Data Table - Using reusable CreateTable */}
              <CreateTable
                tableName="TimeLogs"
                headers={mainTableHeaders}
                accessors={mainTableAccessors}
                data={rawTimeLogData}
                customCellRenderer={customCellRenderer}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={rawTimeLogData.length}
                emptyMessage="No time logs to display"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal - Updated to use CreateTable */}
      <UploadDatFileForm
        isOpen={showUploadForm}
        onClose={closeUploadFormHandleClick}
        data_temp={uploadTimeEntriesDatFile}
        setData_temp={setUploadTimeEntriesDatFile}
        handleFileUpload={handleFileDatFileUpload}
        handleFinalFileUpload={handleFinalDatFileUpload}
        currentPage={currentPageUpload}
        setCurrentPage={setCurrentPageUpload}
        headers={uploadTableHeaders}
        accessors={uploadTableAccessors}
      />

      <ConfirmationModal
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Time Logs"
        message={`Are you sure you want to delete all ${rawTimeLogData.length} time logs from ${dateStartFilter} to ${dateEndFilter}? This action cannot be undone.`}
        confirmText="DELETE"
        cancelText="CANCEL"
        type="danger"
        isLoading={loadingRequest}
      />

      {/* Success/Error Message */}
      <GenerateMessage
        form={showRequestMessage}
        message={isSuccess ? "Request Success!" : requestMessage}
        isSuccess={isSuccess}
      />

      {/* Loading Spinners */}
      <LoadingSpinner loading={isDatUploadLoading || loadingRequest} />
    </div>
  );
};

function UploadDatFileForm({
  isOpen,
  onClose,
  data_temp,
  setData_temp,
  handleFileUpload,
  handleFinalFileUpload,
  currentPage,
  setCurrentPage,
  headers,
  accessors
}) {
  const [files, setFiles] = useState([]);
  const [hasNoError, setHasNoError] = useState(true);
  const [hasValidData, setHasValidData] = useState(false);

  // Check if any of the uploaded files is a PDF
  const isPdfFile = files.some(file => file.name.toLowerCase().endsWith('.pdf'));

  useEffect(() => {
    if (files.length > 0 && data_temp.length === 0) {
      setHasNoError(false);
      setHasValidData(false);
    } else if (data_temp.length > 0) {
      const hasErrors = data_temp.some(item => item.errors && item.errors.length > 0);
      setHasNoError(!hasErrors);

      // Validation logic based on file type
      const validRecords = data_temp.filter(item => {
        if (isPdfFile) {
          // PDF validation: Must have no errors and have a valid date
          return !item.errors && item.date;
        } else {
          // DAT validation
          return !item.errors && item.dateTimeLog;
        }
      });
      setHasValidData(validRecords.length > 0);
    }
  }, [data_temp, files, isPdfFile]);

  // --- PDF Text Extraction Logic ---
  const extractTextFromPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(" ");
      fullText += pageText + "\n";
    }
    return fullText;
  };

  // --- Parser Logic (Converts raw OCR/PDF text to the array schema) ---
  const convertPdfTextToArray = (text) => {
    if (!text) return [];

    // 1. Remove obvious labels and footer text to isolate raw data
    let cleanText = text
      .replace(/Name|Enroll#|Department|Branch|Date|Day|TimeIn|TimeOut|Puregold|Detailed Time Report \(Sequence\)|Period:.*|Run Date :.*/g, '')
      .trim();

    // 2. Extract Employee Metadata (Name, Enroll#, Dept, Branch)
    // Regex: Name (A-Z and punctuation), then digits (3+), then Dept (word/dashes),
    // then Branch (everything until the first Date MM/DD/YYYY)
    const metaRegex = /([A-Z,.\s-]+?)\s+(\d{3,})\s+([\w-]+)\s+(.+?)(?=\s\d{2}\/\d{2}\/\d{4})/;
    const metaMatch = cleanText.match(metaRegex);

    if (!metaMatch) {
      console.error("Metadata not found in: ", cleanText.substring(0, 50));
      return [];
    }

    const currentHeader = {
      name: metaMatch[1].trim(),
      enrollNo: metaMatch[2].trim(),
      department: metaMatch[3].trim(),
      branch: metaMatch[4].trim(),
    };

    // 3. Extract logs (everything after metadata)
    const logsPart = cleanText.substring(metaMatch[0].length).trim();

    // 4. Split by Date (MM/DD/YYYY) - regex keeps the date in the result array
    const segments = logsPart.split(/(\d{2}\/\d{2}\/\d{4})/g).filter(s => s.trim().length > 0);

    const result = [];

    // Segments come in pairs: [Date, ContentFollowingDate]
    for (let i = 0; i < segments.length; i += 2) {
      const date = segments[i];
      const content = segments[i + 1] || "";

      // Extract Day (the first word after the date)
      const dayMatch = content.trim().match(/^([A-Za-z]+)/);
      const day = dayMatch ? dayMatch[1] : "";

      // Extract all timestamps (00:00 AM/PM)
      const times = content.match(/\d{2}:\d{2}\s[AP]M/g) || [];

      result.push({
        ...currentHeader,
        date: date,
        day: day,
        timeIn_1: times[0] || "",
        timeOut_1: times[1] || "",
        timeIn_2: times[2] || "",
        timeOut_2: times[3] || "",
        timeIn_3: times[4] || "",
        timeOut_3: times[5] || "",
      });
    }

    return result;
  };

  // --- File Processing ---
  // --- File Processing (Multiple Files) ---
  const processFiles = async (fileList) => {
    const pdfFiles = fileList.filter(file => file.name.toLowerCase().endsWith('.pdf'));
    const datFiles = fileList.filter(file => file.name.toLowerCase().endsWith('.dat'));

    let allParsedData = [];

    // 1. Process all PDFs
    if (pdfFiles.length > 0) {
      try {
        const results = await Promise.all(pdfFiles.map(async (file) => {
          const extractedText = await extractTextFromPdf(file);
          console.log({extractedText})
          return convertPdfTextToArray(extractedText);
        }));
        
        // Flatten the array of arrays into a single list
        allParsedData = [...allParsedData, ...results.flat()];
      } catch (err) {
        console.error("Failed to parse PDF files:", err);
        allParsedData.push({ errors: ["One or more PDFs could not be parsed."] });
      }
    }

    // 2. Process all DATs (Assuming your handleFileUpload supports cumulative data)
    // If handleFileUpload handles the state internally, you might need to adjust it
    if (datFiles.length > 0) {
      handleFileUpload(datFiles); 
      // Note: If you want to combine DAT and PDF in the same view, 
      // you'll need to modify handleFileUpload to return data instead of setting state.
    }

    if (allParsedData.length > 0) {
      setData_temp(allParsedData);
    }
  };

  const handleBrowse = (e) => {
    const fileList = Array.from(e.target.files);
    setFiles(fileList);
    processFiles(fileList);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fileList = Array.from(e.dataTransfer.files);
    setFiles(fileList);
    processFiles(fileList);
    e.currentTarget.style.backgroundColor = "white";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "#bdbdbd7d";
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "white";
  };

  const clearFiles = () => {
    setFiles([]);
    setData_temp([]);
    const fileInput = document.getElementById('fileInput');
    if (fileInput) fileInput.value = '';
  };

  const submit = () => {
    handleFinalFileUpload(data_temp);
    onClose();
  };

  const handleClose = () => {
    clearFiles();
    onClose();
  };

  // Custom cell renderer for upload preview
  const uploadCellRenderer = (row, accessor) => {
    const value = row[accessor];

    // Format date time for .dat logs
    if (accessor === "dateTimeLog" && value) {
      try {
        const date = new Date(value);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      } catch {
        return value;
      }
    }

    // Highlight Unknown Employee
    if (accessor === "employeePersonalInformationFullName" && value && value.includes("Unknown")) {
      return <span className="text-orange-600 font-medium">{value}</span>;
    }

    // Format error messages
    if (accessor === "errors" && value) {
      return <span className="text-red-600 font-medium">{value}</span>;
    }

    return value || '-';
  };

  return (
    <>
      <div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
        {isOpen && (
          <div className="modal" style={{ maxWidth: '95%', maxHeight: '90%', width: '1300px' }}>
            <div className="popUp-header row">
              <div className="popUp-title">
                <h3 style={{ color: "#668fca" }}>Upload File (Raw Time Logs)</h3>
              </div>
              <div className="popUp-close">
                <CloseIcon onClick={handleClose} className="cursor-pointer hover:opacity-70" />
              </div>
            </div>

            <div className="popUp-body" style={{ overflow: 'auto', maxHeight: '65vh' }}>
              {/* File Upload Area */}
              <div className="mb-6">
                <label htmlFor="fileInput" className='browse cursor-pointer inline-block px-5 py-2.5 bg-[#c57c29] text-white rounded border border-[#a76a25] hover:bg-[#a76a25] transition-colors'>
                  Browse...
                </label>
                <input
                  type="file"
                  id="fileInput"
                  multiple
                  accept=".dat,.pdf"
                  onChange={handleBrowse}
                  className="hidden"
                />

                <div
                  id='drop-files-here'
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragOut}
                  className="border-2 border-dashed border-gray-300 rounded p-10 mt-3 text-center bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Drop .dat or .pdf files here
                </div>

                {files.length > 0 && (
                  <div className="border border-gray-200 p-4 mt-4 rounded">
                    <span className="font-medium text-gray-700">Uploaded File(s):</span>
                    <div className="max-h-32 overflow-y-auto mt-2">
                      <ul className="pl-5">
                        {files.map((file, index) => (
                          <li key={index} className="text-gray-600">{file.name}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex justify-end mt-3">
                      <button
                        className="cancel row px-4 py-2 bg-gray-100 text-gray-600 rounded border border-gray-300 hover:bg-gray-200 transition-colors"
                        onClick={clearFiles}
                      >
                        CLEAR
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Table */}
              {files.length > 0 && (
                <>
                  <div className={`p-3 rounded mb-4 ${hasValidData ? 'bg-green-500' :
                    (data_temp.length > 0 ? 'bg-red-600' : 'bg-yellow-500')
                    } text-white`}>
                    {hasValidData ? "Data is valid and ready for upload." :
                      (data_temp.length > 0 ? "Cannot upload - data contains errors." :
                        "No valid data to upload.")}
                  </div>
                  <div className="max-h-96">
                    <CreateTable
                      tableName="UploadPreview"
                      headers={headers}
                      accessors={accessors}
                      data={data_temp}
                      customCellRenderer={uploadCellRenderer}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      itemsPerPage={10}
                      totalItems={data_temp.length}
                      emptyMessage="No data to preview"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Footer and Buttons */}
            <div className="flex justify-between p-5">
              <div className="flex items-center">
                <span>{data_temp.length.toLocaleString()} item/s</span>
              </div>

              <div className="buttons row justify-end border-t border-gray-200">
                {files.length > 0 && hasNoError && (
                  <button
                    className="save row px-5 py-2.5 bg-[#0062cc] text-white rounded border border-[#004c9c] hover:bg-[#004c9c] transition-colors mr-3"
                    onClick={submit}
                  >
                    UPLOAD
                  </button>
                )}
                <button
                  className="cancel row px-5 py-2.5 bg-gray-100 text-gray-600 rounded border border-gray-300 hover:bg-gray-200 transition-colors"
                  onClick={handleClose}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// GenerateMessage component remains the same
function GenerateMessage({ form, message, isSuccess }) {
  return (
    <>
      <div className={`modal-overlay toggleMessage-overlay ${form ? 'open' : ''}`}>
        {form && (
          <div className="modal toggleMessage" style={{ backgroundColor: isSuccess ? "#E0FFDC" : "#ffd0c5" }}>
            <div className='column'>
              {isSuccess ?
                <div className='request_title row' style={{ fontWeight: "bold", color: "green", alignItems: "center" }}>
                  <CheckCircleIcon />
                  <span>{"SUCCESS MESSAGE"}</span>
                </div>
                :
                <div className='request_title row' style={{ fontWeight: "bold", color: "#DD5746", alignItems: "center" }}>
                  <ErrorIcon />
                  <span>{"ERROR MESSAGE"}</span>
                </div>
              }
              <span style={{ textAlign: "center", padding: "10px 0", color: isSuccess ? "green" : "#DD5746" }}>{message}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function convertToTimeDat(timePart, datePart) {
  if (!datePart || !timePart) return null;

  const timeMatch = timePart.match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
  if (!timeMatch) return null;

  let hour = parseInt(timeMatch[1], 10);
  let minute = parseInt(timeMatch[2], 10);
  let second = parseInt(timeMatch[3], 10);

  if (
    hour < 0 || hour > 23 ||
    minute < 0 || minute > 59 ||
    second < 0 || second > 59
  ) {
    return null;
  }

  const formattedTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;

  // ✅ Correct ISO 8601 format for backend
  return `${datePart}T${formattedTime}`;
}

export default UploadTimeEntriesERE;